import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import { LIKE, EQ } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import SelectInput from "../../framework/SelectInput";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import { executeScroll, isEmpty } from "../../utils/utils";
import { searchHsn } from "../../services/hsn-services";
import { gstValues } from "../../utils/constant";
import NumberInput from "../../framework/NumberInput";

const HsnReportFilter = ({
  reportRef,
  onFilterShow,
  applyFilters = () => {},
  applyFilterOnMount = true,
  refreshData = false,
}) => {
  const [hsnOptions, setHsnOptions] = useState([]);
  const [hsn, setHsn] = useState("");
  const [description, setDescription] = useState("");
  const [gst, setGst] = useState(null);
  const [limit, setLimit] = useState(100);

  const [orderBy] = useState([{ fieldName: "createdTime", orderType: "desc" }]);

  const apr_filter_hsn_code_ref = useRef();
  const applyFilterRef = useRef();
    const filterLimitRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      if (!isEmpty(gst)) {
        filters.push(createFilterParam("gstPercent", gst.id, "number", EQ));
      }
      if (!isEmpty(description)) {
        filters.push(
          createFilterParam("description", description, "string", LIKE)
        );
      }
      if (!isEmpty(hsn)) {
        filters.push(createFilterParam("hsnCode", hsn, "number", LIKE));
      }
      if (!isEmpty(limit)) {
          result.limit = limit;
      }
      if (orderBy) {
        result.orderBy = orderBy;
      }
      if (isFilterChanged) {
        result.filters = filters;
      }
      applyFilters(result, {
        gst,
        description,
        hsn,
        limit,
        orderBy,
      });
      reportRef.current?.applyFilters(result);
    },
    [gst, description, hsn, limit, orderBy, reportRef, applyFilters]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  let debounce = useCallback(
    _.debounce(() => {
      applyFilterHandler();
    }, 300),
    []
  );
  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);

  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="apr_filter_hsn_code"
            label="HSN Code"
            options={hsnOptions}
            isOptionEqualToValue={(option, value) => {
              return option=== value;
            }}
            ref={apr_filter_hsn_code_ref}
            getOptionLabel={(option) => option.label || option}
            onFocus={() => setCurrentComponent("apr_filter_hsn_code")}
            onChange={(e, values) => {
              setHsn(values.label);
            }}
            freeSolo
            value={hsn}
            onInputChange={(e) => {
              if (!e) return;
              setHsn(e.target.value);
              setHsnOptions([]);
              if (!e?.target?.value) setHsn(null);
              if (e?.target?.value?.length > 2) {
                searchHsn(`search=${+e.target.value.trim()}`).then((resp) => {
                  let updatedResp = [];
                  resp.forEach((prod) => {
                    updatedResp.push({
                      id: prod.hsnCodeId,
                      label: prod.hsnCode,
                    });
                  });
                  setHsnOptions(updatedResp);
                });
              }
            }}
            markers={JSON.stringify({
              up: "apr_filter_hsn_code",
              down: "apr_filter_description",
              right: "apr_filter_description",
              left: "apr_filter_hsn_code",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="apr_filter_description"
            label="Description"
            onFocus={() => setCurrentComponent("apr_filter_description")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_hsn_code",
                down: "add_hsn_gst",
                right: "add_hsn_gst",
                left: "apr_filter_hsn_code",
              }),
            }}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="add_hsn_gst"
            name="hsnGst"
            disableClearable={false}
            label="GST %"
            onFocus={() => setCurrentComponent("add_hsn_gst")}
            onChange={(event, option) => {
              setGst(option);
            }}
            getOptionLabel={(option) => {
              return option.label ||option;
            }}
            isOptionEqualToValue={(option, value) => option=== value}
            options={gstValues}
            markers={JSON.stringify({
              up: "apr_filter_description",
              down: "add_limit_filter",
              left: "apr_filter_description",
              right: "add_limit_filter",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="add_limit_filter"
            label="Limit"
            ref={filterLimitRef}
            onValueChange={(values) => setLimit(values.floatValue)}
            isAllowed={(values) => values?.formattedValue <= 999999}
            onFocus={() => {
              executeScroll(filterLimitRef);
              setCurrentComponent("dsr_filter_limit");
            }}
            value={limit}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_hsn_gst",
                down: "apr_apply_filter_button",
                left: "add_hsn_gst",
                right: "apr_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="apr_apply_filter_button"
            label="Apply"
            ref={applyFilterRef}
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => {
              applyFilterHandler(true);
            }}
            markers={JSON.stringify({
              up: "add_limit_filter",
              left: "add_limit_filter",
              right: "apr_apply_filter_button",
              down: "apr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  useEffect(() => {
    if (refreshData) {
      applyFilterHandler(true);
    }
  }, [refreshData, applyFilterHandler]);
  useEffect(() => {
    if (onFilterShow) {
      apr_filter_hsn_code_ref?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);
  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);
  useEffect(() => {
    if (applyFilterOnMount) debounce();
    return () => {
      debounce.cancel();
    };
  }, [applyFilterOnMount, debounce]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default HsnReportFilter;
